<template>
    <form @submit.prevent="onSubmit" class="otp-verification-form">
        <div class="otp-illustration">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="2" width="18" height="20" rx="2" fill="#e6f7fa" />
                <rect x="7" y="6" width="10" height="2" rx="1" fill="#4fd1c5" />
                <circle cx="12" cy="17" r="1" fill="#4fd1c5" />
            </svg>
        </div>
        <h2 class="otp-title">אימות קוד</h2>
        <div class="otp-subtitle">קוד אימות נשלח בדוא״ל. יש להזין את הקוד כדי להמשיך.</div>
        <div class="otp-input-group">
            <input v-for="(digit, idx) in digits" :key="idx" ref="otpInput" type="text" inputmode="numeric"
                maxlength="1" class="otp-input" v-model="digits[idx]" @input="onDigitInput(idx, $event)"
                @keydown="onDigitKeydown(idx, $event)" @paste="onPaste" autocomplete="one-time-code" />
        </div>
        <b-message type="is-danger" v-if="invalidOTP" class="otp-error">
            קוד האימות שגוי. יש לנסות שוב.
        </b-message>
        <b-button :disabled="!isComplete" native-type="submit" type="is-primary">
            אימות קוד
        </b-button>
        <div class="otp-resend">
            <span>הקוד לא הגיע?</span>
            <b-button type="is-text" :disabled="resendCooldown > 0" @click="onResend">
                <span>שליחה מחדש</span>
                <span v-if="resendCooldown > 0"> ({{ resendCooldown }} שניות)</span>
            </b-button>
        </div>
    </form>
</template>

<script>
export default {
    name: 'OtpInput',
    props: {
        invalidOTP: { type: Boolean, default: false },
        resendCooldown: { type: Number, default: 0 },
    },
    data() {
        return {
            digits: Array(6).fill(''),
        };
    },
    mounted() {
        this.$refs.otpInput[0].focus();
    },
    computed: {
        isComplete() {
            return this.digits.every((digit) => digit !== '') && this.digits.length === 6;
        },
    },
    methods: {
        onDigitInput(idx, event) {
            const value = event.target.value.replace(/[^0-9]/g, "");
            this.$set(this.digits, idx, value);
            if (value && idx < 5) {
                this.$refs.otpInput[idx + 1].focus();
            }
        },
        onDigitKeydown(idx, event) {
            if (event.key === "Backspace") {
                this.$emit('clear-otp-error');
                if (!this.digits[idx] && idx > 0) {
                    this.$refs.otpInput[idx - 1].focus();
                }
            }
        },
        onPaste(event) {
            event.preventDefault();
            const paste = event.clipboardData.getData("text").replace(/[^0-9]/g, "");
            for (let i = 0; i < Math.min(paste.length, 6); i += 1) {
                this.$set(this.digits, i, paste[i]);
            }
            this.$nextTick(() => {
                const next = Math.min(paste.length, 6) - 1;
                if (this.$refs.otpInput[next]) this.$refs.otpInput[next].focus();
            });
            this.onSubmit();
        },
        onSubmit() {
            if (this.isComplete) {
                this.$emit("submit", this.digits.join(""));
            }
        },
        onResend() {
            if (this.resendCooldown === 0) {
                this.$emit("resend");
            }
        }
    }
};
</script>

<style scoped>
.otp-verification-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 0 0 0;
}

.otp-illustration {
    margin-bottom: 16px;
}

.otp-title {
    font-size: 25px;
    text-align: right;
    font-weight: normal;
    margin-bottom: 0.5rem;
    color: #222;
}

.otp-subtitle {
    font-size: 1rem;
    color: #7b7b7b;
    margin-bottom: 2rem;
    text-align: center;
}

.otp-input-group {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: center;
    margin-bottom: 1.5rem;
    direction: ltr !important;
}

.otp-input {
    width: 44px;
    height: 54px;
    font-size: 1.7rem;
    text-align: center;
    border: 2px solid #ff7a59;
    border-radius: 8px;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 8px rgba(22, 159, 230, 0.08);
    color: #222;
}

.otp-input:focus {
    border-color: #ffaa95;
    outline: none;
    box-shadow: 0 0 0 2px #fa9076;
}

.otp-verify-btn:hover,
.otp-verify-btn:focus {
    background: #ffa28b !important;
}

.otp-resend {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    white-space: nowrap;
    gap: 4px;
}

.otp-resend span {
    white-space: nowrap;
}

.otp-resend .button {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
}

.otp-resend-btn {
    font-family: sans-serif, Arial, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, Helvetica;
    width: 180px;
    padding: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    border-radius: 4px;
    margin: 1.2rem auto 0 auto;
    display: block;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 8px rgba(22, 159, 230, 0.06);
    border: 2px solid #169fe6;
    background: #fff;
    color: #169fe6;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
    text-align: center;
    cursor: pointer;
}

.otp-resend-btn:hover,
.otp-resend-btn:focus {
    background: #e3f2fd;
    color: #118ccf;
    border-color: #118ccf;
}

.otp-error {
    margin-bottom: 0.5rem;
    width: 100%;
    text-align: center;
}
</style>
